/**
 * Duplicate Invoice Candidates diagnostic card (Commit 2, item 3).
 *
 * Reports invoices sharing (invoice_number, amount, entity_id) -- the same
 * invoice_number + amount appearing as two separate invoice docs was observed
 * producing two separate Suggested-Matches candidates under one transaction.
 * Report only: does not resolve whether a group is doc duplication or two
 * legitimate installments, and makes no writes.
 */

import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function DuplicateInvoiceCandidatesPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);

  var load = async function () {
    setLoading(true);
    try {
      var res = await diagnosticsAPI.duplicateInvoiceCandidates();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Duplicate Invoice Candidates</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Invoices sharing the same invoice number, amount, and entity -- these can
          surface as two separate Suggested Matches candidates under one transaction.
          Report only: doc duplication vs. two legitimate installments is not decided
          here, and no writes happen. Read-only.
        </p>

        <Button onClick={load} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Run Diagnostic
        </Button>

        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">Invoices scanned</p>
                <p className="text-xl font-bold font-mono">{data.invoice_count_scanned}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">Duplicate groups</p>
                <p className="text-xl font-bold font-mono">{data.duplicate_group_count}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] text-muted-foreground">Docs involved</p>
                <p className="text-xl font-bold font-mono">{data.duplicate_doc_count}</p>
              </div>
            </div>

            {data.groups.length > 0 && (
              <div className="space-y-3">
                {data.groups.map(function (g, i) {
                  return (
                    <div key={i} className="rounded-lg border p-3 text-xs">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="font-mono font-medium">
                          {g.invoice_number} -- {g.amount} ({g.entity_id})
                        </span>
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 text-[9px]">
                          {g.doc_count} docs
                        </Badge>
                      </div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-1 pr-2 font-medium">Doc ID</th>
                            <th className="py-1 pr-2 font-medium">Due date</th>
                            <th className="py-1 pr-2 font-medium">Issue date</th>
                            <th className="py-1 pr-2 font-medium">Supplier / client</th>
                            <th className="py-1 pr-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.docs.map(function (d) {
                            return (
                              <tr key={d.id} className="border-b border-border/40">
                                <td className="py-1 pr-2 font-mono truncate max-w-[140px]" title={d.id}>{d.id}</td>
                                <td className="py-1 pr-2">{d.due_date || '--'}</td>
                                <td className="py-1 pr-2">{d.issue_date || '--'}</td>
                                <td className="py-1 pr-2 truncate max-w-[160px]">{d.supplier_client_name || '--'}</td>
                                <td className="py-1 pr-2">{d.matching_status || '--'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
